import { Vector2 } from '../../../odie';

import { CubeAnimation } from '../../animations/CubeAnimation';
import { ColorId, PowerBlockType, powerBlockTypes } from '../../defs/block';
import {
  BlockEntity,
  setBlockEntityViewLayer,
} from '../../entities/BlockEntity';
import {
  despawnOverlayEntity,
  spawnOverlayEntity,
} from '../../entities/OverlayEntity';
import { GameScene } from '../../GameScene';
import {
  blockIsBinded,
  blockIsFrozen,
  blockIterateAll,
} from '../../util/blockTools';
import { getCollisionTracker } from '../../util/CollisionTracker';
import { IEffect } from './IEffect';
import { RadialAttackEffect } from './RadialAttackEffect';
import StateObserver from 'src/StateObserver';
import { lollipopLimit } from 'src/replicant/match2/races/races.ruleset';

// types
//-----------------------------------------------------------------------------
export type CubeEffectOptions = {
  position: Vector2;
  color: ColorId;
};

/*
    cube block clear effect
*/
export class CubeEffect implements IEffect {
  // fields
  //-------------------------------------------------------------------------
  // input
  private readonly _scene: GameScene;
  private _options: CubeEffectOptions;

  // init
  //-------------------------------------------------------------------------
  constructor(scene: GameScene, options: CubeEffectOptions) {
    this._scene = scene;
    this._options = options;
  }

  public static assets(options: Partial<CubeEffectOptions>): string[] {
    return [];
  }

  // impl
  //-------------------------------------------------------------------------
  public async execute() {
    const collisionTracker = getCollisionTracker();
    const targets: BlockEntity[] = [];

    // collect targets
    blockIterateAll(this._scene, (base) => {
      if (this._isTarget(base)) targets.push(base);
    });

    // animate targets
    await this._animate(targets);

    if (this._options.color === 'all') {
      const grid = this._scene.mapDef?.grid;
      const dist = grid ? Math.max(grid.columns, grid.rows) : 9;
      const radius = Math.ceil(Math.sqrt(dist * dist + dist * dist));

      // do a map-wide radial sweep
      const effect = new RadialAttackEffect(this._scene, {
        position: this._options.position,
        radius,
        duration: 0.6,
        // Cube collisions makes destroyed blocks trigger surrounding collisions, like regular tap
        // but the big lollipop should behave more like a bomb, hitting all existing blocks only once
        collider: { type: 'cubeAll' },
      });

      await effect.execute();

      await StateObserver.invoke.addMatch2RaceScore({
        goal: 'lollipop',
        score: Math.min(effect.targetCount, lollipopLimit),
      });
    } else {
      // collide targets
      for (const target of targets) {
        // Do not target frozen blocks
        if (
          blockIsFrozen(this._scene, target) ||
          blockIsBinded(this._scene, target)
        )
          continue;
        collisionTracker.collide(target.c.block, 'attack', { type: 'cube' });
      }

      await StateObserver.invoke.addMatch2RaceScore({
        goal: 'lollipop',
        score: targets.length,
      });
    }

    // notify group break event
    this._scene.events.publish({
      id: 'group',
      cause: 'cube',
      count: targets.length,
    });
  }

  // private: animation
  //-------------------------------------------------------------------------
  private async _animate(targets: BlockEntity[]) {
    const { color, position } = this._options;
    const animation = new CubeAnimation({
      color,
      position,
      targets,
    });
    // spawn animation as overlay entity
    const entity = spawnOverlayEntity(
      this._scene,
      position,
      { width: 1, height: 1 },
      () => animation,
    );

    setBlockEntityViewLayer(this._scene, entity, 'foreground');

    // run animation
    await animation.start();
    // despawn entity when completed
    animation.completed.then(() => despawnOverlayEntity(this._scene, entity));
  }

  // private: support
  //-------------------------------------------------------------------------
  private _isTarget(entity: BlockEntity): boolean {
    const { color } = this._options;
    const block = entity.c.block;

    // not if frozen or binded
    if (
      blockIsFrozen(this._scene, entity) ||
      blockIsBinded(this._scene, entity)
    )
      return false;

    // if targeting all colors, target everything except power blocks
    if (color === 'all') {
      return !powerBlockTypes.includes(block.props.type as PowerBlockType);
      // else target basic blocks matching the cube's color
    } else if (block?.props.type === 'basic' && color === block.props.color) {
      return true;
    }

    return false;
  }
}
